import styled, { css } from 'styled-components';

import { getPurpurBorder, getPurpurColor, getPurpurMotion } from '@telialt/common-theme/src/purpurSelectors';

type DotProps = { $color?: string; $patternUrl?: string };

const fillInnerShadow = 'inset 0 0.0625rem 0.25rem 0.25rem rgba(0, 0, 0, 0.15)';

export const ColorDotLabel = styled.label<DotProps>`
    position: relative;
    display: block;
    width: 2.5rem;
    height: 2.5rem;
    cursor: pointer;
    user-select: none;
    border-radius: ${getPurpurBorder('radiusFull')};
    background-color: ${({ $color }) => $color ?? 'transparent'};
    background-image: ${({ $patternUrl }) => ($patternUrl ? `url('${$patternUrl}')` : 'none')};
    background-size: cover;
    background-position: center;
    box-shadow:
        inset 0 0 0 ${getPurpurBorder('widthMd')} ${getPurpurColor('functionalWhite')},
        ${fillInnerShadow},
        0 0 0 ${getPurpurBorder('widthXs')} ${getPurpurColor('borderInteractiveSubtle')};
    transition: box-shadow ${getPurpurMotion('duration150')} ease-in-out;

    &:hover {
        box-shadow:
            inset 0 0 0 ${getPurpurBorder('widthMd')} ${getPurpurColor('functionalWhite')},
            ${fillInnerShadow},
            0 0 0 ${getPurpurBorder('widthXs')} ${getPurpurColor('borderInteractiveSubtleHover')};
    }
`;

export const Diagonal = styled.span`
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: ${getPurpurBorder('radiusFull')};

    &::after {
        content: '';
        position: absolute;
        top: -50%;
        left: 50%;
        box-sizing: border-box;
        width: 0.21875rem;
        height: 200%;
        border: 0 solid ${getPurpurColor('functionalWhite')};
        border-width: 0 ${getPurpurBorder('widthXs')};
        background: ${getPurpurColor('gray400')};
        transform: rotate(-45deg) translateX(-50%);
        transform-origin: center;
        transition: background ${getPurpurMotion('duration150')} ease-in-out;
    }
`;

const selectedRing = css`
    box-shadow:
        inset 0 0 0 ${getPurpurBorder('widthMd')} ${getPurpurColor('functionalWhite')},
        ${fillInnerShadow};
    outline: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('borderInteractivePrimary')};
    outline-offset: calc(-1 * ${getPurpurBorder('widthXs')});

    ${Diagonal} {
        inset: ${getPurpurBorder('widthXs')};
    }
`;

export const ColorDotWrapper = styled.div`
    display: inline-block;
    position: relative;

    input {
        opacity: 0;
        position: absolute;
        inset: 0;
        margin: 0;
    }

    input:focus:focus-visible ~ ${ColorDotLabel} {
        ${selectedRing}

        &::before {
            content: '';
            position: absolute;
            inset: calc(-1 * (${getPurpurBorder('widthMd')} + ${getPurpurBorder('widthSm')}));
            border-radius: ${getPurpurBorder('radiusFull')};
            border: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('borderInteractiveFocus')};
            pointer-events: none;
        }
    }

    input:checked ~ ${ColorDotLabel} {
        ${selectedRing}
    }

    ${ColorDotLabel}:hover ${Diagonal}::after,
    input:checked ~ ${ColorDotLabel} ${Diagonal}::after,
    input:focus:focus-visible ~ ${ColorDotLabel} ${Diagonal}::after {
        background: ${getPurpurColor('borderInteractivePrimary')};
    }

    input:disabled ~ ${ColorDotLabel} {
        cursor: not-allowed;

        &:hover {
            box-shadow:
                inset 0 0 0 ${getPurpurBorder('widthMd')} ${getPurpurColor('functionalWhite')},
                ${fillInnerShadow},
                0 0 0 ${getPurpurBorder('widthXs')} ${getPurpurColor('borderInteractiveSubtle')};

            ${Diagonal}::after {
                background: ${getPurpurColor('gray400')};
            }
        }
    }

    input:disabled:checked ~ ${ColorDotLabel}:hover {
        ${selectedRing}
    }
`;
